import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Mail, Phone, MapPin, Edit, Trash2, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const Customers = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    if (user?.id) {
      loadCustomers();
    }
  }, [user]);

  const loadCustomers = async () => {
    setIsLoading(true);
    try {
      // Get all estimates and extract unique customers
      const response = await axios.get(`${BACKEND_URL}/api/estimates/`, {
        params: { user_id: user.id }
      });
      
      // Extract unique customers from estimates
      const estimatesData = response.data;
      const customersMap = new Map();
      
      estimatesData.forEach(estimate => {
        const key = estimate.customer_last_name + estimate.customer_first_name + estimate.address;
        if (!customersMap.has(key)) {
          customersMap.set(key, {
            id: estimate.id,
            first_name: estimate.customer_first_name || '',
            last_name: estimate.customer_last_name || 'N/A',
            address: estimate.address,
            total_estimates: 1,
            total_value: estimate.total_cost || 0,
            latest_estimate: estimate.created_at
          });
        } else {
          const existing = customersMap.get(key);
          existing.total_estimates += 1;
          existing.total_value += estimate.total_cost || 0;
        }
      });
      
      setCustomers(Array.from(customersMap.values()));
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase();
    return (
      customer.last_name?.toLowerCase().includes(searchLower) ||
      customer.first_name?.toLowerCase().includes(searchLower) ||
      customer.address?.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-600 mt-1">Manage your customer relationships</p>
        </div>
        <Link to="/dashboard/estimates/new">
          <Button className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Customer
          </Button>
        </Link>
      </div>

      {/* Search Bar */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Search by name or address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Customers Grid */}
      {filteredCustomers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-gray-500">
            <p className="text-lg font-medium mb-2">No customers found</p>
            <p className="text-sm">
              {searchTerm ? 'Try adjusting your search' : 'Create your first estimate to add a customer'}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCustomers.map((customer) => (
            <div key={customer.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {customer.first_name} {customer.last_name}
                  </h3>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-start text-sm text-gray-600">
                  <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-2">{customer.address}</span>
                </div>
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Estimates:</span>
                  <span className="font-semibold text-gray-900">{customer.total_estimates}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Value:</span>
                  <span className="font-semibold text-green-600">{formatCurrency(customer.total_value)}</span>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <Link to={`/dashboard/estimates?customer=${customer.last_name}`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full">
                    <Eye className="w-4 h-4 mr-1" />
                    View Estimates
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Total Customers</div>
          <div className="text-2xl font-bold text-gray-900">{customers.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Total Estimates</div>
          <div className="text-2xl font-bold text-blue-600">
            {customers.reduce((sum, c) => sum + c.total_estimates, 0)}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Total Value</div>
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(customers.reduce((sum, c) => sum + c.total_value, 0))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers;
